import React, { useRef, useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import RichTextEditor from '../../../components/RichTextEditor.js';
import { getApiErrorMessage } from '../../../request/getApiErrorMessage.ts';
import { uploadSopScreenshot } from '../../../request/sop-synthesizer/sopSynthesizer.api.ts';
import {
  editorSegmentsToMarkdown,
  markdownToEditorSegments,
  type EditorSegment,
} from '../sectionBodyFormat.ts';

type Props = {
  sopId: string;
  body: string;
  frames: Record<string, string>;
  saving?: boolean;
  onSave: (markdown: string) => void;
  onCancel: () => void;
};

function EditableTable({
  headers,
  rows,
  onChange,
}: {
  headers: string[];
  rows: string[][];
  onChange: (headers: string[], rows: string[][]) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-gray-200">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-50">
            {headers.map((h, i) => (
              <th key={i} className="border border-gray-200 px-2 py-1.5 text-left font-medium">
                <input
                  className="w-full bg-transparent text-sm font-medium outline-none"
                  value={h}
                  onChange={(e) => {
                    const next = [...headers];
                    next[i] = e.target.value;
                    onChange(next, rows);
                  }}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {headers.map((_, ci) => (
                <td key={ci} className="border border-gray-200 px-2 py-1.5">
                  <input
                    className="w-full bg-transparent text-sm outline-none"
                    value={row[ci] ?? ''}
                    onChange={(e) => {
                      const nextRows = rows.map((r) => [...r]);
                      while (nextRows[ri].length < headers.length) nextRows[ri].push('');
                      nextRows[ri][ci] = e.target.value;
                      onChange(headers, nextRows);
                    }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ACCEPT_IMAGES = 'image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif';

/** Issues-style section edit: rich text + editable tables + screenshots. */
const SopSectionEditor: React.FC<Props> = ({ sopId, body, frames, saving = false, onSave, onCancel }) => {
  const [segments, setSegments] = useState<EditorSegment[]>(() => markdownToEditorSegments(body));
  const [localFrames, setLocalFrames] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const allFrames = { ...frames, ...localFrames };

  const updateSeg = (index: number, next: EditorSegment) => {
    setSegments((prev) => prev.map((s, i) => (i === index ? next : s)));
  };

  const removeSeg = (index: number) => {
    setSegments((prev) => prev.filter((_, i) => i !== index));
  };

  const onPickFiles = async (files: FileList | null) => {
    if (!files?.length || uploading || saving) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const res = await uploadSopScreenshot(sopId, file);
        setLocalFrames((prev) => ({ ...prev, [res.id]: res.url }));
        setSegments((prev) => [...prev, { type: 'image', caption: '', frameId: res.id }]);
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not upload image'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const busy = saving || uploading;

  return (
    <div className="space-y-4">
      {segments.map((seg, index) => {
        if (seg.type === 'prose') {
          return (
            <div key={index} className="rounded-md border border-gray-200 bg-white p-3">
              <RichTextEditor
                value={seg.html}
                onChange={(html: string) => updateSeg(index, { type: 'prose', html })}
                onBlur={() => {}}
                readOnly={false}
                minRows={4}
                placeholder="Edit section text…"
              />
            </div>
          );
        }
        if (seg.type === 'table') {
          return (
            <EditableTable
              key={index}
              headers={seg.headers}
              rows={seg.rows}
              onChange={(headers, rows) => updateSeg(index, { type: 'table', headers, rows })}
            />
          );
        }
        const url = allFrames[seg.frameId];
        return (
          <figure key={index} className="relative rounded-md border border-gray-200 bg-gray-50 p-3 pr-10">
            <button
              type="button"
              disabled={busy}
              onClick={() => removeSeg(index)}
              className="absolute right-2 top-2 rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-50"
              aria-label="Remove screenshot"
              title="Remove screenshot"
            >
              <X className="h-4 w-4" />
            </button>
            {url ? (
              <img src={url} alt={seg.caption || ''} className="max-h-64 rounded border border-gray-200" />
            ) : (
              <p className="text-xs italic text-gray-400">[screenshot unavailable]</p>
            )}
            {seg.caption ? <figcaption className="mt-1 text-xs text-gray-500">{seg.caption}</figcaption> : null}
          </figure>
        );
      })}

      <div>
        <p className="mb-2 text-[11px] font-semibold tracking-wide text-gray-500">SCREENSHOTS</p>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT_IMAGES}
          multiple
          className="hidden"
          onChange={(e) => void onPickFiles(e.target.files)}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="flex h-28 w-full items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="inline-flex items-center gap-2">
            <ImagePlus className="h-4 w-4" />
            {uploading ? 'Uploading…' : 'Add image'}
          </span>
        </button>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => onSave(editorSegmentsToMarkdown(segments))}
          className="rounded-md bg-blue-800 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className="text-sm text-gray-600 hover:underline disabled:opacity-50">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default SopSectionEditor;
